import { createFileRoute } from '@tanstack/react-router'
import { useState, useCallback } from 'react'
import { apiEndpoints, APP_NAME, APP_PUBLIC_URL } from '@/lib/env'
import { CheckCircle, XCircle, AlertCircle, Upload, FileText, Key, Clock, Shield, ExternalLink, Building2, Cloud } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import glydeComboDark from '@/assets/glyde-combo-dark.svg'
import glydeCombo from '@/assets/glyde-combo.svg'
import { useTheme } from '@/context/theme-context'
import { ScrollArea } from '@/components/ui'

export const Route = createFileRoute('/evidence/verify/')({
  component: EvidenceVerifyPage,
})

interface VerificationCheck {
  name: string
  passed: boolean
  details?: string
}

interface FileCheck extends VerificationCheck {}

interface VerificationResult {
  valid: boolean
  checks: {
    manifestSignature: VerificationCheck
    tsaTimestamp: VerificationCheck | null
    fileSignatures?: FileCheck[]
  }
  details: {
    signedAt?: string
    tsaTimestampedAt?: string
    publicKeyFingerprint?: string
    serverKeyFingerprint?: string
    algorithm?: string
    filesVerified?: number
    filesTotal?: number
    manifestVersion?: string
  }
  errors: string[]
  warnings: string[]
}

function EvidenceVerifyPage() {

  const { theme } = useTheme()
  const [manifestJson, setManifestJson] = useState('')
  const [zipBase64, setZipBase64] = useState<string | null>(null)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)
  const [result, setResult] = useState<VerificationResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const extractFingerprint = useCallback((): string | null => {
    try {
      const parsed = JSON.parse(manifestJson || '{}')
      return parsed?.cryptographic_signature?.publicKeyFingerprint || null
    } catch {
      return null
    }
  }, [manifestJson])

  const handleVerify = useCallback(async () => {
    if (!manifestJson.trim() && !zipBase64) {
      setError('Please upload a ZIP or paste a manifest')
      return
    }

    setIsVerifying(true)
    setError(null)
    setResult(null)

    try {
      const response = await fetch(`${apiEndpoints.root}/api/signing/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          zipBase64
            ? { zipBase64, manifestJson: manifestJson || undefined }
            : { manifestJson }
        ),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Verification failed: ${response.status}`)
      }

      const data = await response.json()
      setResult(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setIsVerifying(false)
    }
  }, [manifestJson, zipBase64])

  // Safe base64 conversion for ZIP without spreading large buffers
  const fileToBase64 = useCallback(async (file: File) => {
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        const base64 = result.split(',')[1] || ''
        resolve(base64)
      }
      reader.onerror = () => reject(reader.error || new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }, [])

  const handleFileDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files[0]
    if (!file) return

    try {
      if (file.name.endsWith('.zip')) {
        const JSZip = (await import('jszip')).default
        const zip = await JSZip.loadAsync(file)
        const manifestFile = zip.file('manifest.json')
        if (!manifestFile) {
          setError('No manifest.json found in ZIP file')
          return
        }
        const content = await manifestFile.async('string')
        setManifestJson(content)
        const base64 = await fileToBase64(file)
        setZipBase64(base64)
        setUploadedFileName(file.name)
      } else if (file.name.endsWith('.json')) {
        // Handle JSON file
        const content = await file.text()
        setManifestJson(content)
        setZipBase64(null)
        setUploadedFileName(file.name)
      } else {
        setError('Please upload a .zip or .json file')
      }
    } catch (err) {
      setError('Failed to read file')
    }
  }, [])

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      if (file.name.endsWith('.zip')) {
        const JSZip = (await import('jszip')).default
        const zip = await JSZip.loadAsync(file)
        const manifestFile = zip.file('manifest.json')
        if (!manifestFile) {
          setError('No manifest.json found in ZIP file')
          return
        }
        const content = await manifestFile.async('string')
        setManifestJson(content)
        const base64 = await fileToBase64(file)
        setZipBase64(base64)
        setUploadedFileName(file.name)
      } else if (file.name.endsWith('.json')) {
        const content = await file.text()
        setManifestJson(content)
        setZipBase64(null)
        setUploadedFileName(file.name)
      } else {
        setError('Please upload a .zip or .json file')
      }
    } catch (err) {
      setError('Failed to read file')
    }
  }, [])

  return (
    <div className="h-full bg-background">
      <ScrollArea className='h-full' viewClassName='[&>div]:!block'>

     
      {/* Top Navigation Bar */}
      <div className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href={APP_PUBLIC_URL} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img loading="lazy" src={
              theme === 'dark'
                ? glydeComboDark
                : glydeCombo
            } alt={APP_NAME} className="h-6" />
          </a>
          <a
            href={APP_PUBLIC_URL}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            Back to {APP_NAME}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12 pb-24">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 mb-4">
            <Shield className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold mb-3">Evidence Pack Verification</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Verify the authenticity and integrity of {APP_NAME} evidence packs.
            Upload a ZIP file or paste the manifest.json to validate cryptographic signatures and TSA timestamps.
          </p>
        </div>

        {/* Upload Area */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Upload Evidence Pack</CardTitle>
            <CardDescription>
              Drop an evidence pack ZIP file or manifest.json, or paste the manifest content below
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Drop Zone */}
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center transition-colors mb-4",
                isDragging ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
              )}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleFileDrop}
            >
              <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground/90 mb-2">
                Drag and drop your evidence pack here
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Supports .zip (evidence pack) or .json (manifest)
              </p>
              <label>
                <input
                  type="file"
                  accept=".zip,.json"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button variant="outline" asChild>
                  <span>Browse Files</span>
                </Button>
              </label>
            </div>

            {/* Manual Input */}
            <div>
              <label className="text-sm font-medium  text-muted-foreground">
                Or paste manifest.json content:
              </label>
              <Textarea
                value={manifestJson}
                onChange={(e) => setManifestJson(e.target.value)}
                placeholder='{"evidence_pack_id": "ep_...", "cryptographic_signature": {...}}'
                className="font-mono text-sm h-40 mt-2"
              />
              {uploadedFileName && (
                <p className="text-xs text-muted-foreground mt-1">
                  Loaded from: {uploadedFileName} {zipBase64 ? '(ZIP - full verification)' : '(JSON - manifest only)'}
                </p>
              )}
            </div>

            {/* Verify Button */}
            <div className="mt-4 flex justify-end">
              <Button
                onClick={handleVerify}
                disabled={isVerifying || (!manifestJson.trim() && !zipBase64)}
                className="min-w-32"
              >
                {isVerifying ? 'Verifying...' : 'Verify Signature'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent >
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Verification Error</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results Display */}
        {result && (
          <Card className={cn(
            "mb-6",
            result.valid ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
          )}>
            <CardHeader>
              <div className="flex items-center gap-3">
                {result.valid ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : (
                  <XCircle className="h-8 w-8 text-red-500" />
                )}
                <div>
                  <CardTitle className={result.valid ? "text-green-800" : "text-red-800"}>
                    {result.valid ? 'Verification Passed' : 'Verification Failed'}
                  </CardTitle>
                  <CardDescription className={result.valid ? "text-green-600" : "text-red-600"}>
                    {result.valid
                      ? 'This evidence pack has valid cryptographic signatures'
                      : 'This evidence pack failed signature verification'}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Signature Checks */}
              <div className="space-y-3">
                <h3 className="font-medium text-slate-700">Verification Checks</h3>

                {/* Manifest Signature */}
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                  {result.checks.manifestSignature.passed ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-slate-800">{result.checks.manifestSignature.name}</p>
                    <p className="text-sm text-slate-500">{result.checks.manifestSignature.details}</p>
                  </div>
                  <Badge variant={result.checks.manifestSignature.passed ? "default" : "destructive"} className={`${result.checks.manifestSignature.passed ? ' ' : '!bg-red-500/10'}`}>
                   <span className={`${result.checks.manifestSignature.passed ? '!text-black' : '!text-red-500'}`}>{result.checks.manifestSignature.passed ? 'PASSED' : 'FAILED'}</span>
                  </Badge>
                </div>
                {/* TSA Timestamp */}
                {result.checks.tsaTimestamp && (
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    {result.checks.tsaTimestamp.passed ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{result.checks.tsaTimestamp.name}</p>
                      <p className="text-sm text-slate-500">{result.checks.tsaTimestamp.details}</p>
                    </div>
                    <Badge variant={result.checks.tsaTimestamp.passed ? "default" : "destructive"} className={`${result.checks.tsaTimestamp.passed ? ' ' : '!bg-red-500/10'}`}>
                     <span className={`${result.checks.tsaTimestamp.passed ? '!text-black' : '!text-red-500'}`}>{result.checks.tsaTimestamp.passed ? 'PASSED' : 'FAILED'}</span>
                    </Badge>
                  </div>
                )}

                {/* File Signatures (ZIP verification) */}
                {result.checks.fileSignatures && result.checks.fileSignatures.length > 0 && (
                  <div className="py-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2 mb-2 px-3">
                      <FileText className="h-4 w-4 text-slate-500" />
                      <p className="font-medium text-slate-800">
                        File Signatures ({result.details.filesVerified ?? 0}/{result.details.filesTotal ?? result.checks.fileSignatures.length})
                      </p>
                    </div>
                    
                    <ScrollArea viewClassName=" [&>div]:!block [&>div]:space-y-2 max-h-64 px-3">
                      {result.checks.fileSignatures.map((check, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-2 border rounded">
                          {check.passed ? (
                            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-mono text-slate-800 break-all">{check.name}</p>
                            <p className="text-xs text-slate-500">{check.details}</p>
                          </div>
                          <Badge variant={check.passed ? "default" : "destructive"} className={`${check.passed ? ' ' : '!bg-red-500/10'}`}   >
                           <span className={`${check.passed ? '!text-black' : '!text-red-500'}`}>{check.passed ? 'PASSED' : 'FAILED'}</span>
                          </Badge>
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                )}
              </div>

              {/* Signature Details */}
              {result.details && (
                <div className="space-y-3">
                  <h3 className="font-medium text-slate-700">Signature Details</h3>
                  <div className="grid grid-cols-2 gap-4 p-4 bg-white rounded-lg border">
                    {result.details.publicKeyFingerprint && (
                      <div className="flex items-start gap-2">
                        <Key className="h-4 w-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Manifest Signed With Key</p>
                          <p className="font-mono text-sm text-black">{result.details.publicKeyFingerprint}</p>
                          {result.details.serverKeyFingerprint &&
                           result.details.serverKeyFingerprint !== result.details.publicKeyFingerprint && (
                            <p className="text-xs text-amber-600 mt-1">
                              Server has different key: {result.details.serverKeyFingerprint}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {result.details.algorithm && (
                      <div className="flex items-start gap-2">
                        <Shield className="h-4 w-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Algorithm</p>
                          <p className="font-mono text-sm !text-black">{result.details.algorithm}</p>
                        </div>
                      </div>
                    )}
                    {result.details.signedAt && (
                      <div className="flex items-start gap-2">
                        <Clock className="h-4 w-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Signed At</p>
                          <p className="text-sm !text-black">{new Date(result.details.signedAt).toLocaleString()}</p>
                        </div>
                      </div>
                    )}
                    {result.details.tsaTimestampedAt && (
                      <div className="flex items-start gap-2">
                        <Clock className="h-4 w-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">TSA Timestamp</p>
                          <p className="text-sm !text-black">{new Date(result.details.tsaTimestampedAt).toLocaleString()}</p>
                        </div>
                      </div>
                    )}
                    {result.details.manifestVersion && (
                      <div className="flex items-start gap-2">
                        <FileText className="h-4 w-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Manifest Version</p>
                          <p className="text-sm !text-black">{result.details.manifestVersion}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {result.warnings && result.warnings.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-medium text-slate-700">Warnings</h3>
                  {result.warnings.map((warning, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5" />
                      <p className="text-sm text-amber-700">{warning}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Errors */}
              {result.errors && result.errors.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-medium text-slate-700">Errors</h3>
                  {result.errors.map((err, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <XCircle className="h-4 w-4 text-red-500 mt-0.5" />
                      <p className="text-sm text-red-700">{err}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Public Key & How It Works */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Key className="h-5 w-5" />
                Public Key
              </CardTitle>
              <CardDescription>
                Download the platform public key to verify signatures independently
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const fp = extractFingerprint()
                    const url = fp
                      ? `${apiEndpoints.root}/api/signing/public-key?fingerprint=${fp}`
                      : `${apiEndpoints.root}/api/signing/public-key`

                    const response = await fetch(url)
                    const data = await response.json()
                    const blob = new Blob([data.publicKeyPem], { type: 'text/plain' })
                    const blobUrl = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = blobUrl
                    a.download = `screenata-public-key-${data.fingerprint}.pem`
                    a.click()
                    URL.revokeObjectURL(blobUrl)
                  } catch {
                    setError('Failed to download public key')
                  }
                }}
              >
                Download Public Key (PEM)
              </Button>
              <p className="text-xs text-muted-foreground">
                Enterprise customers using BYOK may have a different public key.
                Check the <code className="bg-foreground/10 px-1 rounded">public_key_fingerprint</code> in the manifest to identify which key was used.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                How It Works
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-3">
              <p>
                <strong>Cryptographic Signing:</strong> Each evidence pack manifest is signed using
                RSA-SHA256 with the platform's private key, ensuring tampering is detectable.
              </p>
              <p>
                <strong>TSA Timestamps:</strong> Signatures are timestamped by RFC 3161 Time Stamp
                Authorities, providing independent proof of when the evidence was created.
              </p>
              <p>
                <strong>Verification:</strong> Anyone with the public key can verify that the
                evidence has not been modified since signing.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* CLI Verification */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Command-Line Verification</CardTitle>
            <CardDescription>
              Verify signatures offline using OpenSSL
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-900 text-slate-100 p-4 rounded-lg font-mono text-sm overflow-x-auto">
              <p className="text-slate-400"># Extract manifest from ZIP</p>
              <p>unzip -p evidence-pack.zip manifest.json {'>'} manifest.json</p>
              <p className="mt-2 text-slate-400"># Download public key</p>
              <p>
                curl -s {apiEndpoints.root}/api/signing/public-key{`${
                  extractFingerprint() ? `?fingerprint=${extractFingerprint()}` : ''
                }`} | jq -r .publicKeyPem {'>'} public.pem
              </p>
              <p className="mt-2 text-slate-400"># Extract and verify signature (requires jq)</p>
              <p>jq -r '.cryptographic_signature.signature' manifest.json | base64 -d {'>'} signature.bin</p>
              <p>jq 'del(.cryptographic_signature)' manifest.json | openssl dgst -sha256 -verify public.pem -signature signature.bin</p>
            </div>
          </CardContent>
        </Card>

        {/* BYOK Enterprise Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Enterprise: Bring Your Own Key (BYOK)
            </CardTitle>
            <CardDescription>
              Enterprise customers can use their own signing keys for full control
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {APP_NAME} supports three signing modes to meet different security and compliance requirements:
            </p>

            <div className="grid gap-4">
              {/* Platform Mode */}
              <div className="flex gap-3 p-3 bg-background/10 rounded-lg border">
                <div className="flex-shrink-0">
                  <Shield className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Platform Signing</p>
                  <p className="text-sm text-muted-foreground">
                    Default mode. Evidence is signed using {APP_NAME}'s managed keys. Suitable for most customers.
                  </p>
                </div>
              </div>

              {/* Customer Key Mode */}
              <div className="flex gap-3 p-3 bg-background/10 rounded-lg border">
                <div className="flex-shrink-0">
                  <Key className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Customer Key Upload</p>
                  <p className="text-sm text-muted-foreground">
                    Upload your own RSA or ECDSA signing key. The key is encrypted at rest using AES-256-GCM.
                    You maintain ownership while {APP_NAME} handles the signing infrastructure.
                  </p>
                </div>
              </div>

              {/* Cloud KMS Mode */}
              <div className="flex gap-3 p-3 bg-background/10 rounded-lg border">
                <div className="flex-shrink-0">
                  <Cloud className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Cloud KMS Integration</p>
                  <p className="text-sm text-muted-foreground">
                    Keep your private key in your own cloud KMS (AWS KMS, Google Cloud KMS, or Azure Key Vault).
                    The key never leaves your infrastructure, meeting the strictest compliance requirements.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground">
                BYOK is available for Enterprise plan customers. Contact your account manager to configure custom signing keys.
                All signing modes support the same verification process - the manifest includes the public key fingerprint for verification.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-border text-center">
          <a href={APP_PUBLIC_URL} className="inline-block mb-4 hover:opacity-80 transition-opacity">
            <img loading="lazy" src={
              theme === 'dark'
                ? glydeComboDark
                : glydeCombo
            } alt={APP_NAME} className="h-5 mx-auto" />
          </a>
          <p className="text-sm text-muted-foreground">
            {APP_NAME} provides AI-powered compliance evidence collection with cryptographic verification.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            RFC 3161 Time Stamp Authority integration ensures independent, tamper-proof timestamps.
          </p>
        </div>
      </div>
      </ScrollArea>
    </div>
  )
}
